/**
 * S19. Project settings, and the one thing that cannot be undone.
 *
 * A single column of bordered sections, each with a heading, one control and
 * one consequence sentence. The shape is the discipline: a section that wants
 * two controls has to become two sections and name them both, which is the
 * argument it should have been having anyway.
 *
 * THE DANGER ZONE IS ENTIRELY SIMPLE, and that is a hard constraint rather
 * than a layout choice. Nothing correctness critical is ever Advanced only, so
 * every consequence sentence about deleting a project is on the page in Simple
 * mode, before the button, whether or not anybody ever turns Advanced on.
 *
 * THE ENUMERATION MIXES THE SURVIVAL WITH THE LOSSES. One list: your code on
 * GitHub stays, your database goes, your files go, your web address is
 * released. A list of only losses reads as a scare screen, and a person cannot
 * tell from it whether their repository is about to go with the project.
 *
 * TYPE TO CONFIRM IS RESERVED FOR THE IRREVERSIBLE, so it is here and nowhere
 * else on this screen. Pausing does not ask for typing. Renaming does not ask
 * for typing. Using the gesture on reversible actions is exactly what trains
 * people to type through the one dialog that matters.
 *
 * THE CLEANING UP STATE. After the confirm, the dialog is replaced in place by
 * a narrated checklist rather than closing. A dialog that vanished would leave
 * a person staring at an unchanged page wondering whether the thing they were
 * afraid of actually happened. The checklist is clock derived like every other
 * asynchronous thing in the product; it runs on a local script because
 * scripts.ts has no project-delete entry yet, and the day it does, this
 * collapses onto AsyncNarration with no change to what is on screen.
 *
 * THE WEB ADDRESS IS NOT EDITABLE, and that is a fact about the platform
 * rather than a screen decision. A project's subdomain is attached to a live
 * Cloudflare custom domain and baked into the starter the repo was seeded
 * with; changing it is a reprovision, not a column write, and cloud-api has
 * no route for it. So the address is printed, copyable, with no field and no
 * Save: a text box beside a Save button is a promise, and this one could
 * never be kept.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=paused    the paused project, with the strip and the dimmed page
 *   ?state=delete    the delete dialog, name not yet typed
 *   ?state=deleting  the cleaning up checklist, held on screen
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Dialog } from '../../components/primitives/Dialog';
import { Field } from '../../components/primitives/Field';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { ConsequenceList } from '../../components/patterns/ConsequenceList';
import type { ConsequenceItem } from '../../components/patterns/ConsequenceList';
import { CopyField } from '../../components/patterns/CopyField';
import { CustomDomainSection } from '../../components/patterns/CustomDomainSection';
import { dateLabel } from '../../components/patterns/DeployRow';
import { DestructiveConfirm } from '../../components/patterns/DestructiveConfirm';
import { MembersSection } from '../../components/patterns/MembersSection';
import { PageCacheSection } from '../../components/patterns/PageCacheSection';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { StagingSection } from '../../components/patterns/StagingSection';
import { Skeleton } from '../../components/patterns/Skeleton';
import { StepLedger } from '../../components/patterns/AsyncNarration';
import type { NarrationStep } from '../../components/patterns/AsyncNarration';

import { environmentOf, getProject } from '../../lib/api';
import {
  deleteProject,
  getRun,
  pauseProject,
  renameProject,
  resumeProject,
  type Run,
} from '../../lib/real-api';
import { ApiError } from '../../lib/http';
import { durationSeconds } from '../../lib/format';
import { useSessionStore } from '../../lib/auth/session';
import { useAccount } from '../../lib/auth/use-app-data';
import { useAsync } from '../../lib/hooks/use-async';
import { useFlash } from '../../lib/hooks/use-flash';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { useWallClock } from '../../lib/hooks/use-wall-clock';
import { t } from '../../lib/i18n/t';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Project, Speed, StepStatus } from '../../lib/types';

import '../../styles/components/settings.css';

const STATES = ['resting', 'paused', 'delete', 'deleting', 'loading'] as const;
type StateName = (typeof STATES)[number];

/**
 * The cleaning up script. Four steps, in the order the platform actually does
 * them: the app comes down first, because that is the one a person is anxious
 * about, and the two irreversible deletions happen last.
 */
const DELETE_STEPS: { label: string; ms: number }[] = [
  { label: 'Taking your app offline', ms: 3_000 },
  { label: 'Releasing your web address', ms: 2_500 },
  { label: 'Setting your database aside', ms: 5_000 },
  { label: 'Setting your files aside', ms: 4_000 },
  { label: 'Finishing up', ms: 2_000 },
];

const DELETE_TOTAL = DELETE_STEPS.reduce((sum, step) => sum + step.ms, 0);

/** Clock derived, never accumulated. Instant lands the whole run at once. */
function elapsedOf(startedAt: number, now: number, speed: Speed): number {
  if (speed === 'instant') return DELETE_TOTAL;
  return Math.min(DELETE_TOTAL, Math.max(0, (now - startedAt) * speed));
}

function deleteLedger(elapsed: number): NarrationStep[] {
  let start = 0;
  return DELETE_STEPS.map((step) => {
    const end = start + step.ms;
    const status: StepStatus =
      elapsed >= end ? 'succeeded' : elapsed > start ? 'running' : 'pending';
    const shown =
      status === 'succeeded'
        ? durationSeconds(step.ms)
        : status === 'running'
          ? durationSeconds(elapsed - start)
          : undefined;
    start = end;
    return { label: t(step.label), status, elapsed: shown };
  });
}

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/**
 * How long cloud-api keeps a deleted project's database and files before its
 * nightly purge destroys them. Mirrors that Worker's PROJECT_RETENTION_DAYS
 * var, which is the source of truth. The wire does not carry the number, so
 * an environment that changes it has to change this too.
 */
const RETENTION_DAYS = 30;

/** cloud-api's TEARDOWN_STEPS (services/runs.ts), in the same vocabulary the
 * mock's own DELETE_STEPS labels use. Deleting no longer destroys the data:
 * it sets it aside for RETENTION_DAYS, and these labels say so. */
const REAL_DELETE_STEP_LABEL: Record<string, string> = {
  'worker.delete': 'Taking your app offline',
  'domain.detach': 'Releasing your web address',
  'database.retain': 'Setting your database aside',
  'storage.retain': 'Setting your files aside',
  finalize: 'Finishing up',
};

const DEFAULT_TEARDOWN_STEP_LABELS = [
  'Taking your app offline',
  'Releasing your web address',
  'Setting your database aside',
  'Setting your files aside',
  'Finishing up',
] as const;

function initialDeleteLedger(): NarrationStep[] {
  return DEFAULT_TEARDOWN_STEP_LABELS.map((label, index) => ({
    label: t(label),
    status: index === 0 ? 'running' : 'pending',
  }));
}

function realDeleteLedger(run: Run): NarrationStep[] {
  const runActive = run.status === 'queued' || run.status === 'running';
  let firstPendingPassed = false;
  return run.steps.map((step) => {
    const retrying = step.status === 'failed' && runActive;
    let status: StepStatus = step.status;
    if (retrying) {
      status = 'running';
    } else if (
      runActive &&
      status === 'pending' &&
      !firstPendingPassed &&
      !run.steps.some((s) => s.status === 'running')
    ) {
      status = 'running';
      firstPendingPassed = true;
    }
    return {
      label: t(REAL_DELETE_STEP_LABEL[step.key] ?? step.key),
      status,
      detail: retrying ? t('retrying') : undefined,
    };
  });
}

/* ------------------------------------------------------------------ screen */

export default function Settings() {
  const slug = useProjectSlug() ?? '';
  const account = useAccount();
  const navigate = useNavigate();
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const speed = useDemoStore((s) => s.prefs.speed);
  const { state } = useScreenState<StateName>(STATES, 'resting');

  const { data, isLoading, refetch } = useAsync(async () => getProject(slug), [slug, scenarioId]);

  const [name, setName] = useState('');
  const [savedName, flashName] = useFlash();
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pausedLocal, setPausedLocal] = useState<boolean | null>(null);
  const [pausing, setPausing] = useState(false);
  const [pauseError, setPauseError] = useState<string | null>(null);
  // The project as the last pause or resume answered it, so the page changes
  // in place instead of dropping back to a skeleton for a refetch.
  const [fresh, setFresh] = useState<Project | null>(null);
  const [deletingAt, setDeletingAt] = useState<number | null>(null);
  const upsertProject = useSessionStore((s) => s.upsertProject);

  // Real mode's own delete-run state, kept separate from deletingAt/clock
  // above so the mock's elapsed-time "finished" effect (below) never fires
  // off a fake timer for a teardown that's still actually running.
  const [realDeleteRun, setRealDeleteRun] = useState<Run | null>(null);
  const [realDeleteError, setRealDeleteError] = useState<string | null>(null);

  const deleteStartedAtRef = useRef<number>(0);
  const deletePollTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (deletePollTimerRef.current !== undefined) {
        window.clearTimeout(deletePollTimerRef.current);
      }
    };
  }, []);

  // The fields are seeded from the record once it lands, and then they are the
  // person's, not the fixture's. A field that reset itself on every read would
  // throw away a rename halfway through being typed.
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setNameError(null);
    setPausedLocal(null);
    setPauseError(null);
    setFresh(null);
    setConfirmOpen(state === 'delete');
    setDeletingAt(state === 'deleting' ? Date.now() : null);
  }, [data, state]);

  const clock = useWallClock(deletingAt !== null);
  const elapsed = deletingAt === null ? 0 : elapsedOf(deletingAt, clock, speed);
  const finished = deletingAt !== null && elapsed >= DELETE_TOTAL;

  // A deep linked cleaning up state stays on screen, because a state that
  // navigated away the moment it finished would be a state nobody could
  // screenshot. The real flow lands on the projects list.
  useEffect(() => {
    if (!finished || state === 'deleting') return;
    navigate('/projects');
  }, [finished, state, navigate]);

  useEffect(() => {
    if (data) {
      const paused = pausedLocal ?? (state === 'paused' || data.status === 'paused');
      captureEvent('settings_viewed', {
        project_slug: data.slug,
        is_paused: paused,
      });
    }
  }, [data, pausedLocal, state]);

  if (isLoading || state === 'loading') {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Settings')}</h1>
        </header>
        <Skeleton shape="panel" count={3} label={t('Loading your settings')} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Settings')}</h1>
        </header>
        <Notice tone="quiet" title={t('That project is not here.')}>
          <p>{t('It may have been deleted, or the address may have a typo in it.')}</p>
        </Notice>
        <p>
          <Link className="link-inline" to="/projects">
            {t('All projects')}
          </Link>
        </p>
      </div>
    );
  }

  const project = fresh ?? data;
  const base = `/p/${encodeURIComponent(project.slug)}`;
  const environment = environmentOf(project);
  const oldHost = environment.webAddress.replace(/^https?:\/\//, '');
  const paused = pausedLocal ?? (state === 'paused' || project.status === 'paused');
  // A quota pause is the account's allowance, not a switch on this page: only
  // credits, a plan change or the cycle reset lift it, so the page says so and
  // offers Usage instead of a button the server would refuse.
  const quotaPaused = paused && project.paused?.reason === 'quota';

  const consequences: ConsequenceItem[] = [
    { kind: 'survives', text: t("Your code stays on GitHub. We don't touch your repository.") },
    {
      kind: 'survives',
      text: t(
        'Your database and files are kept for {days} days, then permanently deleted. Getting them back before then means asking us, and there is no undo button yet.',
        { days: RETENTION_DAYS },
      ),
    },
    { kind: 'loss', text: t('Your app goes offline immediately.') },
    {
      kind: 'loss',
      text: t('{address} stops working and becomes available for someone else to use.', {
        address: oldHost,
      }),
    },
  ];

  function pollDeleteRun(runId: string) {
    deleteStartedAtRef.current = Date.now();
    let stale = false;

    function nextDelay(): number {
      const elapsed = Date.now() - deleteStartedAtRef.current;
      if (elapsed < 15_000) return 1000;
      if (elapsed < 60_000) return 2000;
      return 5000;
    }

    async function poll() {
      if (deletePollTimerRef.current !== undefined) {
        window.clearTimeout(deletePollTimerRef.current);
        deletePollTimerRef.current = undefined;
      }
      try {
        const run = await getRun(runId);
        if (stale) return;
        setRealDeleteRun(run);
        if (run.status === 'queued' || run.status === 'running') {
          deletePollTimerRef.current = window.setTimeout(poll, nextDelay());
        } else if (run.status === 'succeeded') {
          navigate('/projects');
        }
      } catch {
        if (!stale) {
          setRealDeleteError(t('Lost track of the deletion. Reload this page to check on it.'));
        }
      }
    }

    function onVisible() {
      if (document.visibilityState === 'visible') void poll();
    }

    void poll();
    document.addEventListener('visibilitychange', onVisible);
  }

  /**
   * The one write on this page that is not destructive, and until now the
   * one that did nothing: Save flashed "Saved" and never called anything.
   * The name is pushed into the shell's own project copy too, so the rail
   * and the switcher rename with it instead of waiting for a reload.
   */
  function saveName() {
    const next = name.trim();
    if (!project || next === '' || next === project.name) return;
    if (USE_MOCK) {
      flashName();
      return;
    }
    setSavingName(true);
    setNameError(null);
    captureEvent('project_rename_submitted', { project_slug: project.slug });
    renameProject(project.id, next)
      .then((updated) => {
        upsertProject(updated);
        flashName();
        captureEvent('project_rename_succeeded', { project_slug: updated.slug });
      })
      .catch((err: unknown) => {
        const message =
          err instanceof ApiError ? err.message : t("That name couldn't be saved. Try again.");
        setNameError(message);
        captureEvent('project_rename_failed', {
          project_slug: project.slug,
          error_code: err instanceof ApiError ? err.code : 'unknown',
          error_message: message,
        });
      })
      .finally(() => setSavingName(false));
  }

  /**
   * Pause and resume are reversible, so no dialog and no typing: the button is
   * the whole gesture. The holding page goes up (or comes down) within seconds
   * of the answer, and the shell's copy of the project is updated so the rail
   * and the overview agree without a reload.
   */
  function setPaused(next: boolean) {
    if (!project) return;
    if (USE_MOCK) {
      setPausedLocal(next);
      return;
    }
    setPausing(true);
    setPauseError(null);
    captureEvent(next ? 'project_pause_submitted' : 'project_resume_submitted', {
      project_slug: project.slug,
    });
    (next ? pauseProject(project.id) : resumeProject(project.id))
      .then((updated) => {
        upsertProject(updated);
        setFresh(updated);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof ApiError ? err.message : t("That didn't work. Try again.");
        setPauseError(message);
        captureEvent(next ? 'project_pause_failed' : 'project_resume_failed', {
          project_slug: project.slug,
          error_code: err instanceof ApiError ? err.code : 'unknown',
        });
      })
      .finally(() => setPausing(false));
  }

  function confirmDelete() {
    // The one irreversible CTA on the screen, and until now the only one with
    // no telemetry at all. `retention_days` rides along because what the
    // person just agreed to is "kept for N days, then gone", and a later change
    // to that number is the kind of thing whose effect on delete rates we
    // would want to be able to see.
    captureEvent('project_delete_confirmed', {
      project_slug: project.slug,
      retention_days: RETENTION_DAYS,
      mock: USE_MOCK,
    });
    if (USE_MOCK) {
      setDeletingAt(Date.now());
      return;
    }
    deleteProject(project.id)
      .then(({ runId }) => pollDeleteRun(runId))
      .catch((err: unknown) => {
        captureEvent('project_delete_failed', {
          project_slug: project.slug,
          reason: err instanceof ApiError ? err.code : 'unknown',
        });
        setRealDeleteError(
          err instanceof ApiError ? err.message : t('Something went wrong deleting this project.'),
        );
      });
  }

  const narration = USE_MOCK ? (
    <div className="set__cleanup">
      <p className="set__cleanup-say">
        {t('Cleaning up. This takes a few seconds and you can leave this page.')}
      </p>
      <StepLedger steps={deleteLedger(elapsed)} />
    </div>
  ) : (
    <div className="set__cleanup">
      <p className="set__cleanup-say">
        {realDeleteError
          ? realDeleteError
          : t('Cleaning up. This takes a few seconds and you can leave this page.')}
      </p>
      <StepLedger steps={realDeleteRun ? realDeleteLedger(realDeleteRun) : initialDeleteLedger()} />
    </div>
  );

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Settings')}</h1>
      </header>

      {/* Flat, and it carries the screen's one ink block, because on a paused
          project switching it back on is the only thing anybody came here to
          do. Nothing is wrong, so nothing is tinted. */}
      {paused ? (
        <section className="set__strip" aria-label={t('This project is paused')}>
          <p className="set__strip-say">
            {quotaPaused
              ? t(
                  'This project is paused because your account used up its allowance. Your web address shows a holding page until you add credits, change your plan, or the allowance resets.',
                )
              : t('This project is paused. Your web address shows a holding page.')}
          </p>
          {quotaPaused ? (
            <Button variant="solid" onClick={() => navigate(`${base}/usage`)}>
              {t('See usage')}
            </Button>
          ) : (
            <Button variant="solid" disabled={pausing} onClick={() => setPaused(false)}>
              {pausing ? t('Turning it back on') : t('Turn it back on')}
            </Button>
          )}
        </section>
      ) : null}

      <div className="set" data-paused={paused ? 'yes' : 'no'}>
        <SectionPanel
          title={t('Project name')}
          note={t("This is just the name you see. Your web address doesn't change.")}
        >
          <div className="set__control">
            <Field
              label={t('Project name')}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setNameError(null);
              }}
              error={nameError ?? undefined}
              disabled={savingName}
            />
            <div className="set__actions">
              <Button
                variant="outline"
                onClick={saveName}
                loading={savingName}
                disabled={name.trim() === '' || name.trim() === project.name}
              >
                {t('Save')}
              </Button>
              <span className="set__saved" role="status" aria-live="polite">
                {savedName ? t('Saved') : ''}
              </span>
            </div>
          </div>
        </SectionPanel>

        <SectionPanel
          title={t('Web address')}
          note={t('Your web address is fixed when your project is created and cannot be changed.')}
        >
          <div className="set__control">
            {/* Printed and copyable, never a field. There is no route that
                changes a subdomain: it is attached to a live custom domain
                and written into the starter the repo was seeded with. */}
            <CopyField label={t('Web address')} value={environment.webAddress} />
            <p className="set__say">
              {t(
                'If you need a different address, make a new project with the name you want and connect the same repository to it.',
              )}
            </p>
          </div>
        </SectionPanel>

        {/* A custom domain is additive to the fixed web address above, and only
            on the live app. A staging environment keeps its SV Cloud address. */}
        {project.environment === 'production' ? (
          <CustomDomainSection project={project} />
        ) : null}

        {/* Teams. Membership belongs to the live app; a staging environment
            is reached by the same people, so it has no list of its own. */}
        {project.environment === 'production' && account.teamsEnabled ? (
          <MembersSection project={project} />
        ) : null}

        {/* The permanent door onto the bundle screen. It is not on the rail
            (see routes.tsx): the answer is "up to date" almost every time
            anybody could look, and the overview raises a notice on the days
            it is not. This section is how somebody finds it on purpose. */}
        <SectionPanel
          title={t('Bundle version')}
          note={t(
            'Your app is built on a bundle we keep improving. Updating it is how your app gets things the platform can now do.',
          )}
        >
          <div className="set__control">
            <div className="set__actions">
              <Button
                variant="outline"
                onClick={() => navigate(`/p/${encodeURIComponent(project.slug)}/bundle`)}
              >
                {t('Check the bundle')}
              </Button>
            </div>
          </div>
        </SectionPanel>

        {/* Only on a project that IS the live app. A staging environment does
            not get one of its own: it is an environment of the app above it,
            not somewhere you would add a third.

            Shown when the feature flag is on, OR when this project already has
            a staging environment even though the flag is now off, so a legacy
            staging env keeps its disable/manage escape hatch. The "Add a
            staging environment" button inside is what the flag actually gates
            (see StagingSection). */}
        {project.environment === 'production' &&
        (account.tenantStagingEnabled || Boolean(project.environments.staging)) ? (
          <StagingSection project={project} onChanged={refetch} />
        ) : null}

        <PageCacheSection
          project={project}
          paused={paused}
          onChanged={(updated) => {
            upsertProject(updated);
            setFresh(updated);
          }}
        />

        {/* Real since 2026-10-25 (apps/cloud F22): pausing swaps the app's code
            for a holding page under its own name, the same mechanism as the
            quota cutoff, and resuming puts the app's own latest version back.
            The demo build still flips a local flag. */}
        <SectionPanel
          title={t('Pause')}
          note={t(
            'Your app stops running and your web address shows a holding page. Your database, your files, and your code all stay exactly as they are. New deploys wait until you turn it back on, and you can do that any time.',
          )}
        >
          <div className="set__control">
            {quotaPaused ? (
              <Notice tone="quiet">
                <p>
                  {t(
                    "This project is already paused because your account used up its allowance, so there's nothing to turn on here.",
                  )}
                </p>
              </Notice>
            ) : null}
            {pauseError ? (
              <Notice tone="attention">
                <p>{pauseError}</p>
              </Notice>
            ) : null}
            <div className="set__actions">
              <Button
                variant="outline"
                disabled={pausing || quotaPaused}
                onClick={() => setPaused(!paused)}
              >
                {pausing
                  ? paused
                    ? t('Turning it back on')
                    : t('Pausing')
                  : paused
                    ? t('Turn it back on')
                    : t('Pause this project')}
              </Button>
            </div>
          </div>
        </SectionPanel>

        <AdvancedDisclosure>
          <div className="set__adv">
            <dl className="idents">
              <div className="idents__row">
                <dt className="idents__key">{t('Project ID')}</dt>
                <dd className="idents__value">
                  <CopyField label={t('Project ID')} value={project.id} size="inline" />
                </dd>
              </div>
              <div className="idents__row">
                <dt className="idents__key">{t('App name')}</dt>
                <dd className="idents__value">
                  <CopyField label={t('App name')} value={project.slug} size="inline" />
                </dd>
              </div>
              <div className="idents__row">
                <dt className="idents__key">{t('Region')}</dt>
                <dd className="idents__value mono">{project.region}</dd>
              </div>
              <div className="idents__row">
                <dt className="idents__key">{t('Created')}</dt>
                <dd className="idents__value mono">{dateLabel(project.createdAt)}</dd>
              </div>
            </dl>

          </div>
        </AdvancedDisclosure>
      </div>

      {/* The danger zone. A tinted border, never a tinted fill, and it is the
          one section on the page that does not dim when the project is paused:
          a paused project can still be deleted, and hiding that would be a
          screen deciding what a person is allowed to do next. */}
      <section className="danger" aria-labelledby="danger-heading">
        <h2 className="danger__title" id="danger-heading">
          {t('Delete this project')}
        </h2>

        {/* Teams: only the owner may delete, and cloud-api refuses anybody
            else with PROJECT_OWNER_REQUIRED. A collaborator is told who can,
            instead of being offered a button whose only outcome is a refusal.
            A staging environment is not the project, so it stays deletable. */}
        {project.role === 'collaborator' && project.environment === 'production' ? (
          <p className="set__say">
            {t('Only the owner of this app can delete it. You can leave it from People above.')}
          </p>
        ) : (
          <>
            <ConsequenceList items={consequences} label={t('What happens when you delete this')} />

            <div className="danger__actions">
              {/* Flat outline, no beacon, no glow. Destructive actions never glow. */}
              <Button variant="danger-outline" onClick={() => setConfirmOpen(true)}>
                {t('Delete this project')}
              </Button>
            </div>
          </>
        )}
      </section>

      <DestructiveConfirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title={t('Delete {project}?', { project: project.name })}
        objectName={project.name}
        confirmLabel={t('Delete {project}', { project: project.name })}
        typeToConfirm
        consequences={consequences}
        narration={narration}
      />

      {/* The deep linked cleaning up state, mounted on its own so the priority
          state in the spec is a link rather than a sequence of clicks. It
          renders the same ledger the dialog renders. */}
      <Dialog
        open={state === 'deleting'}
        onClose={() => navigate(`${base}/settings`)}
        title={t('Delete {project}?', { project: project.name })}
      >
        {narration}
      </Dialog>
    </div>
  );
}
